import { bianka, ReportActivityOptions } from '@fiverr-private/obs';
import { Click, ClickType, ElementType, Impression } from '@fiverr-private/bianka_js';
import { ComplianceUiExtension } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/compliance/compliance_ui_extension';
import { ComplianceElement } from '@fiverr-private/bianka_js/protobuf/bianka/enums/compliance/compliance_element';
import { TierType } from '@fiverr-private/bianka_js/protobuf/bianka/enums/compliance/tier_type';
import { getContext } from '@fiverr-private/fiverr_context';
import { ComplianceTypes } from '../components/ComplianceModal/types';

const getTier = (discountInPercents: number) => {
    if (discountInPercents === 100) {
        return TierType.FREE;
    }

    if (discountInPercents > 0 && discountInPercents < 100) {
        return TierType.DISCOUNT;
    }

    return TierType.FULL_PRICE;
};

export function reportClickEvent({
    eventName,
    elementName,
    sellerId,
    pageName,
    additionalData,
}: {
    eventName: string;
    elementName: string;
    pageName: string;
    sellerId?: number;
    additionalData?: ReportActivityOptions<Click, ComplianceUiExtension>['additionalData'];
}) {
    const { organizationId, userId, pageCtxId } = getContext();

    const eventToReport: ReportActivityOptions<Click> = {
        type: Click,
        page: {
            name: pageName,
            ctxId: pageCtxId,
        },
        additionalData,
        event: {
            type: eventName,
            clickType: ClickType.UNSPECIFIED,
            organization: { id: organizationId ?? '' },
            element: { name: elementName, type: ElementType.UNSPECIFIED },
            ...(userId && { buyer: { id: userId } }),
            ...(sellerId && { seller: { id: sellerId } }),
        },
    };

    bianka.reportActivity(eventToReport);
}

export const reportImpressionEvent = ({
    eventName,
    elementName,
    sellerId,
    pageName,
    additionalData,
}: {
    eventName: string;
    elementName: string;
    pageName: string;
    sellerId?: number;
    additionalData?: ReportActivityOptions<Impression, ComplianceUiExtension>['additionalData'];
}) => {
    const { pageCtxId, userId, organizationId } = getContext();

    bianka.reportActivity<Impression>({
        type: Impression,
        page: {
            ctxId: pageCtxId,
            name: pageName,
        },
        additionalData,
        event: {
            type: eventName,
            user: {
                id: userId as number,
            },
            ...(sellerId && { seller: { id: sellerId } }),
            component: {
                name: elementName,
                impId: '',
                type: '',
                level: 0,
                isLeaf: false,
                positionInParent: 0,
                sizeInParent: 0,
                visibleThroughScroll: false,
            },
            organization: {
                id: organizationId as string,
            },
        },
    });
};

export const reportEventsApi = {
    clickedOnRequestAudit: ({
        pageName,
        sellerId,
        type,
    }: {
        pageName: string;
        sellerId?: number;
        type: 'badge' | 'section';
    }) => {
        reportClickEvent({
            eventName: 'clicked_on_request_audit',
            elementName: 'request_audit',
            pageName,
            sellerId,
            additionalData: {
                type: ComplianceUiExtension,
                message: {
                    complianceElement: type === 'section' ? ComplianceElement.SECTION : ComplianceElement.TOOLTIP,
                    tierType: TierType.UNSPECIFIED,
                },
            },
        });
    },
    clickedOnSubmitWorkerClassificationQuestionnaire: ({
        pageName,
        sellerId,
    }: {
        pageName: string;
        sellerId?: number;
    }) => {
        reportClickEvent({
            eventName: 'clicked_on_submit_worker_classification_questionnaire',
            elementName: 'submit_worker_classification_questionnaire',
            pageName,
            sellerId,
        });
    },
    clickedOnClassificationModalBack: ({
        pageName,
        sellerId,
        discountInPercents,
    }: {
        pageName: string;
        sellerId?: number;
        discountInPercents: number;
    }) => {
        const tierType = getTier(discountInPercents);

        reportClickEvent({
            eventName: 'clicked_on_classification_modal_back',
            elementName: 'classification_modal_back',
            pageName,
            sellerId,
            additionalData: {
                type: ComplianceUiExtension,
                message: { tierType, complianceElement: ComplianceElement.UNSPECIFIED },
            },
        });
    },
    clickedOnBackgroundCheck: ({
        pageName,
        sellerId,
        type,
    }: {
        pageName: string;
        sellerId?: number;
        type: 'badge' | 'section';
    }) => {
        reportClickEvent({
            eventName: 'clicked_on_background_check',
            elementName: 'background_check',
            pageName,
            sellerId,
            additionalData: {
                type: ComplianceUiExtension,
                message: {
                    complianceElement: type === 'section' ? ComplianceElement.SECTION : ComplianceElement.TOOLTIP,
                    tierType: TierType.UNSPECIFIED,
                },
            },
        });
    },
    clickedOnSendDocument: ({ pageName, sellerId }: { pageName: string; sellerId?: number }) => {
        reportClickEvent({
            eventName: 'clicked_on_send_document',
            elementName: 'send_document',
            pageName,
            sellerId,
            additionalData: {
                type: ComplianceUiExtension,
                message: { complianceElement: ComplianceElement.TOOLTIP, tierType: TierType.UNSPECIFIED },
            },
        });
    },
    clickOnLegalDocument: ({
        pageName,
        sellerId,
        type,
    }: {
        pageName: string;
        sellerId?: number;
        type: 'badge' | 'section';
    }) => {
        reportClickEvent({
            eventName: 'clicked_on_legal_document',
            elementName: 'legal_document',
            pageName,
            sellerId,
            additionalData: {
                type: ComplianceUiExtension,
                message: {
                    complianceElement: type === 'section' ? ComplianceElement.SECTION : ComplianceElement.TOOLTIP,
                    tierType: TierType.UNSPECIFIED,
                },
            },
        });
    },
    clickContinueToPayment: ({ pageName, sellerId }: { pageName: string; sellerId?: number }) => {
        reportClickEvent({
            eventName: 'clicked_on_modal_request_leagal_docs_continue_payment',
            elementName: 'modal_request_leagal_docs_continue_payment',
            pageName,
            sellerId,
        });
    },
    clickedOnExpandedFirstTimeBanner: ({ pageName, sellerId }: { pageName: string; sellerId?: number }) => {
        reportClickEvent({
            eventName: 'clicked_on_expanded_first_time_banner',
            elementName: 'expanded_first_time_banner',
            pageName,
            sellerId,
        });
    },
    onPaymentClick: ({
        pageName,
        sellerId,
        complianceType,
        discountInPercents,
    }: {
        pageName: string;
        sellerId?: number;
        complianceType: ComplianceTypes;
        discountInPercents: number;
    }) => {
        let eventName = '';

        let elementName = '';

        const isFree = discountInPercents === 100;

        const tierType = getTier(discountInPercents);

        if (complianceType === ComplianceTypes.WORKFORCE_CLASSIFICATION && isFree) {
            eventName = 'clicked_on_classification_modal_send_request';
            elementName = 'classification_modal_send_request';
        }

        if (complianceType === ComplianceTypes.WORKFORCE_CLASSIFICATION && !isFree) {
            eventName = 'clicked_on_classification_modal_payment';
            elementName = 'classification_modal_payment';
        }

        if (complianceType === ComplianceTypes.BACKGROUND_CHECK && isFree) {
            eventName = 'clicked_on_background_check_send_request';
            elementName = 'background_check_send_request';
        }

        if (complianceType === ComplianceTypes.BACKGROUND_CHECK && !isFree) {
            eventName = 'clicked_on_background_check_modal_payment';
            elementName = 'background_check_modal_payment';
        }

        reportClickEvent({
            eventName,
            elementName,
            pageName,
            sellerId,
            additionalData: {
                type: ComplianceUiExtension,
                message: { tierType, complianceElement: ComplianceElement.UNSPECIFIED },
            },
        });
    },
    onClassificationQuestionView: ({ pageName, sellerId }: { pageName: string; sellerId?: number }) => {
        reportImpressionEvent({
            eventName: 'viewed_worker_classification_questionnaire',
            elementName: 'worker_classification_questionnaire',
            pageName,
            sellerId,
        });
    },
    onModalView: ({
        pageName,
        sellerId,
        discountInPercents,
        complianceType,
    }: {
        pageName: string;
        sellerId?: number;
        discountInPercents: number;
        complianceType: ComplianceTypes;
    }) => {
        let eventName = '';

        let elementName = '';

        const tierType = getTier(discountInPercents);

        if (complianceType === ComplianceTypes.BACKGROUND_CHECK) {
            eventName = 'viewed_background_check_modal';
            elementName = 'background_check_modal';
        }

        if (complianceType === ComplianceTypes.WORKFORCE_CLASSIFICATION) {
            eventName = 'viewed_request_classification_modal';
            elementName = 'request_classification_modal';
        }

        reportImpressionEvent({
            eventName,
            elementName,
            pageName,
            sellerId,
            additionalData: {
                type: ComplianceUiExtension,
                message: { tierType, complianceElement: ComplianceElement.UNSPECIFIED },
            },
        });
    },
    onModalClose: ({
        pageName,
        sellerId,
        discountInPercents,
        complianceType,
        step,
    }: {
        pageName: string;
        sellerId?: number;
        discountInPercents: number;
        complianceType: ComplianceTypes;
        step: number;
    }) => {
        let eventName = '';

        let elementName = '';

        const tierType = getTier(discountInPercents);

        if (complianceType === ComplianceTypes.WORKFORCE_CLASSIFICATION && step === 0) {
            eventName = 'clicked_on_cancel_worker_classification_questionnaire';
            elementName = 'cancel_worker_classification_questionnaire';
        }

        if (complianceType === ComplianceTypes.WORKFORCE_CLASSIFICATION && step === 1) {
            eventName = 'clicked_on_classification_modal_cancel';
            elementName = 'classification_modal_cancel';
        }

        if (complianceType === ComplianceTypes.BACKGROUND_CHECK) {
            eventName = 'clicked_on_background_check_modal_cancel';
            elementName = 'background_check_modal_cancel';
        }

        reportClickEvent({
            eventName,
            elementName,
            pageName,
            sellerId,
            additionalData: {
                type: ComplianceUiExtension,
                message: { tierType, complianceElement: ComplianceElement.UNSPECIFIED },
            },
        });
    },
};
